/**
 * SessionCompanionBar - Session monitoring and control UI
 * GW-4620: Full implementation with polling, countdown, and actions
 *
 * Provides real-time session status monitoring with visual states:
 * - active: Default styling, > 5 minutes remaining
 * - warning: Gold border/text, 1-5 minutes remaining
 * - critical: Red pulsing border/text, < 1 minute remaining
 * - expired/ended: Session ended, show return to marketplace
 * - offline: Dashed border, ~ prefix, API connectivity issues
 */

import { useState, useEffect, useRef, useCallback } from 'react';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@gw/ui';
import { SessionStatus } from '@gw/ui/types';
import { ChevronDown, ChevronUp, Plus, X, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { sessionsApi } from '@/api/sessions';
import { SessionExtensionModal } from '@/components/sessions/SessionExtensionModal';
import { useSessionMonitor } from '@/hooks/useSessionMonitor';

import type { ApplicationSession } from '@gw/ui/types';

interface SessionCompanionBarProps {
  session: ApplicationSession | null;
  appTabRef?: Window | null;
  onSessionEnd?: () => void;
}

export function SessionCompanionBar({
  session,
  appTabRef,
  onSessionEnd,
}: SessionCompanionBarProps) {
  const navigate = useNavigate();

  // Session monitoring hook
  const {
    session: monitoredSession,
    remainingSeconds,
    status,
    isPolling: _isPolling,
    failureCount: _failureCount,
    startMonitoring,
    stopMonitoring,
  } = useSessionMonitor();

  // Local state
  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      return localStorage.getItem('gw_companion_minimized') === 'true';
    } catch {
      return false; // Defensive fallback for localStorage errors
    }
  });
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isEndDialogOpen, setIsEndDialogOpen] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Track which notification thresholds have already fired (reset per session)
  const notifiedWarningRef = useRef(false);
  const notifiedCriticalRef = useRef(false);

  // Request notification permission on mount (non-blocking)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Send a browser notification (best-effort, never throws)
  const sendBrowserNotification = useCallback((title: string, body: string) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const n = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'gw-session-expiry', // replaces previous notification
        requireInteraction: true,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      // Notification constructor can throw in some environments
    }
  }, []);

  // Notify the app tab via postMessage (best-effort)
  const notifyAppTab = useCallback(
    (type: string, remainingSec: number) => {
      if (!appTabRef || appTabRef.closed) return;
      try {
        appTabRef.postMessage({ type, remainingSeconds: remainingSec }, '*');
      } catch {
        // Cross-origin — OK
      }
    },
    [appTabRef]
  );

  // Progressive notification escalation based on status changes
  useEffect(() => {
    if (status === 'warning' && !notifiedWarningRef.current) {
      notifiedWarningRef.current = true;
      const mins = Math.ceil(remainingSeconds / 60);
      // In-app toast (always visible)
      toast.warning(`Session expires in ${mins} minute${mins !== 1 ? 's' : ''}`, {
        description: 'Click Extend to add more time.',
        duration: 10000,
      });
      // Browser notification (if permission granted)
      sendBrowserNotification(
        'Session expiring soon',
        `Your session expires in ${mins} minute${mins !== 1 ? 's' : ''}. Click to extend.`
      );
      notifyAppTab('gw:session-warning', remainingSeconds);
    }

    if (status === 'critical' && !notifiedCriticalRef.current) {
      notifiedCriticalRef.current = true;
      // In-app toast (always visible)
      toast.error('Session expiring in less than 1 minute!', {
        description: 'Extend now or your session will end.',
        duration: Infinity,
        id: 'session-critical', // prevent duplicates
      });
      // Browser notification (if permission granted)
      sendBrowserNotification(
        'Session expiring in less than 1 minute',
        'Switch to General Wisdom now to extend your session.'
      );
      notifyAppTab('gw:session-critical', remainingSeconds);
      // Redirect the app tab back to GW so the user can extend their session.
      // window.focus() is blocked by browsers, but we can set location.href
      // on windows we opened via window.open().
      if (appTabRef && !appTabRef.closed) {
        const sessionId = monitoredSession?.id ?? session?.id ?? '';
        const params = new URLSearchParams({
          action: 'extend',
          sessionId,
        });
        try {
          // Same-origin: capture app URL for return-to-app after extend
          params.set('returnTo', appTabRef.location.href);
        } catch {
          // Cross-origin: can't read location.href, returnTo omitted
        }
        appTabRef.location.href =
          window.location.origin + '/personal/sessions?' + params.toString();
      }
    }

    // Reset flags and dismiss persistent toasts when session returns to active
    if (status === 'active' || status === 'idle') {
      notifiedWarningRef.current = false;
      notifiedCriticalRef.current = false;
      toast.dismiss('session-critical');
    }
  }, [
    status,
    remainingSeconds,
    sendBrowserNotification,
    notifyAppTab,
    appTabRef,
    monitoredSession?.id,
    session?.id,
  ]);

  // Effect to start/stop monitoring based on session prop
  useEffect(() => {
    if (session) {
      startMonitoring(session);
    } else {
      stopMonitoring();
    }

    // Cleanup on unmount
    return () => {
      stopMonitoring();
    };
  }, [session, startMonitoring, stopMonitoring]);

  // Notify the app tab via postMessage and close it
  const closeAppTab = useCallback(() => {
    if (!appTabRef || appTabRef.closed) return;
    try {
      appTabRef.postMessage({ type: 'gw:session-ended' }, '*');
    } catch {
      // Cross-origin postMessage may fail silently — that's OK
    }
    // Give the SDK a moment to handle the message before closing
    setTimeout(() => {
      try {
        appTabRef.close();
      } catch {
        // Cross-origin tabs may throw when closing
      }
    }, 500);
  }, [appTabRef]);

  // Effect to handle session expiry
  useEffect(() => {
    if (status === 'expired' || monitoredSession?.status === SessionStatus.Expired) {
      closeAppTab();
      onSessionEnd?.();
      navigate('/personal/sessions');
      toast.error('Your session has expired and been closed.');
    }
  }, [status, monitoredSession?.status, appTabRef, navigate, onSessionEnd, closeAppTab]);

  // Effect to persist minimize state
  useEffect(() => {
    try {
      localStorage.setItem('gw_companion_minimized', String(isMinimized));
    } catch {
      // Ignore localStorage errors
    }
  }, [isMinimized]);

  // Don't render if no session
  if (!session) {
    return null;
  }

  // Format remaining time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get display session (prefer monitored session for real-time updates)
  const displaySession = monitoredSession || session;

  // Derive visual styling based on status
  const getBorderClass = (): string => {
    switch (status) {
      case 'warning':
        return 'border-warning';
      case 'critical':
        return 'border-destructive';
      case 'offline':
        return 'border-dashed border-muted';
      default:
        return 'border-border';
    }
  };

  const getTimerClass = (): string => {
    switch (status) {
      case 'warning':
        return 'text-warning';
      case 'critical':
        return 'text-destructive animate-pulse';
      case 'offline':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  // Handle minimize/expand
  const handleToggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Handle extend session
  const handleExtendClick = () => {
    if (actionInProgress) return; // Concurrent action guard
    setIsExtendModalOpen(true);
  };

  // Handle end session
  const handleEndClick = () => {
    if (actionInProgress) return; // Concurrent action guard
    setIsEndDialogOpen(true);
  };

  const handleConfirmEnd = async () => {
    setActionInProgress(true);
    setIsEndDialogOpen(false);

    try {
      await sessionsApi.revokeSession(displaySession.id);
      closeAppTab();
      stopMonitoring();
      onSessionEnd?.();
      toast.success('Session has been successfully ended.');
      navigate('/personal/sessions');
    } catch (_error) {
      toast.error('Failed to end session. Please try again.');
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle return to marketplace
  const handleReturnToMarketplace = () => {
    navigate('/personal/sessions');
  };

  // Render different content based on status
  const renderContent = () => {
    if (
      status === 'expired' ||
      status === 'ended' ||
      displaySession.status === SessionStatus.Expired ||
      displaySession.status === SessionStatus.Revoked
    ) {
      return (
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-muted-foreground h-5 w-5" />
            <span
              data-testid="companion-bar-ended-label"
              className="text-muted-foreground text-sm font-medium"
            >
              Session Ended
            </span>
          </div>
          <Button
            data-testid="companion-bar-return-btn"
            onClick={handleReturnToMarketplace}
            variant="default"
            size="sm"
            className="shrink-0"
          >
            Return to Marketplace
          </Button>
        </div>
      );
    }

    if (isMinimized) {
      return (
        <div className="flex w-full items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            {status === 'offline' && (
              <Badge variant="secondary" className="text-xs">
                Offline
              </Badge>
            )}
            <span
              data-testid="companion-bar-timer"
              className={`font-mono text-sm ${getTimerClass()}`}
            >
              {status === 'offline' ? '~' : ''}
              {formatTime(remainingSeconds)}
            </span>
          </div>
          <Button
            data-testid="companion-bar-expand-btn"
            onClick={handleToggleMinimize}
            variant="ghost"
            size="sm"
            aria-label="Expand companion bar"
            className="h-8 w-8 p-0"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    return (
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {displaySession.applicationId} {/* Would be app name in real implementation */}
            </span>
            <div className="flex items-center space-x-2">
              {status === 'offline' && (
                <Badge variant="secondary" className="text-xs">
                  Offline
                </Badge>
              )}
              <span
                data-testid="companion-bar-timer"
                className={`font-mono text-lg ${getTimerClass()}`}
              >
                {status === 'offline' ? '~' : ''}
                {formatTime(remainingSeconds)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            data-testid="companion-bar-extend-btn"
            onClick={handleExtendClick}
            variant="outline"
            size="sm"
            disabled={actionInProgress}
            className="shrink-0"
          >
            <Plus className="mr-1 h-4 w-4" />
            Extend
          </Button>
          <Button
            data-testid="companion-bar-end-btn"
            onClick={handleEndClick}
            variant="destructive"
            size="sm"
            disabled={actionInProgress}
            className="shrink-0"
          >
            <X className="mr-1 h-4 w-4" />
            End Session
          </Button>
          <Button
            data-testid="companion-bar-minimize-btn"
            onClick={handleToggleMinimize}
            variant="ghost"
            size="sm"
            aria-label="Minimize companion bar"
            className="h-8 w-8 p-0"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        data-testid="session-companion-bar"
        data-session-status={status}
        data-session-id={displaySession.id}
        className={`bg-background fixed right-0 bottom-0 left-0 z-50 border-t p-4 ${getBorderClass()} ${isMinimized ? 'h-12' : ''} `}
      >
        {renderContent()}
      </div>

      {/* Extend Session Modal — server-priced (D26: ExtendSessionModal's
          client-calculated cost is deleted; SessionExtensionModal fetches
          GET /sessions/{id}/extension-cost and posts the extend itself). */}
      <SessionExtensionModal
        session={displaySession}
        isOpen={isExtendModalOpen}
        onClose={() => setIsExtendModalOpen(false)}
        onExtended={() => {
          setIsExtendModalOpen(false);
          toast.success('Session extended.');
        }}
      />

      {/* End Session Confirmation Dialog */}
      <Dialog open={isEndDialogOpen} onOpenChange={setIsEndDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to end this session? This action cannot be undone and will
              immediately terminate your access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="companion-bar-end-cancel-btn"
              variant="outline"
              onClick={() => setIsEndDialogOpen(false)}
              disabled={actionInProgress}
            >
              Cancel
            </Button>
            <Button
              data-testid="companion-bar-end-confirm-btn"
              variant="destructive"
              onClick={handleConfirmEnd}
              disabled={actionInProgress}
            >
              End Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
